// Shared constants for the training experience.

/** Copy for TrainingSessionRouter's non-session states. */
export const TRAINING_ROUTER_COPY = {
  preparing: 'Preparing your session...',
  loadFailedTitle: 'We could not load this training track',
  loadFailedBody: 'Please check your connection and try again.',
  retry: 'Try again',
  backToTraining: 'Back to Training',
} as const;

/**
 * Copy for the status panel every training page shows where the live call
 * shows "Audio Settings" (TrainingStatusPanel).
 */
export const TRAINING_CALL_COPY = {
  /** The live call's own panel title, so the two panels read the same. */
  title: 'Audio Settings',
  /** Both modes' end screens: the admin portal's Training page. */
  viewResults: 'View results',
  start: 'Start training',
  cancel: 'Cancel',
  inactive: 'Training inactive',
  connecting: 'Connecting you to the customer...',
  /** Button-row label while waiting for the customer's first word. */
  connectingButton: 'Connecting...',
  customerSilent: 'The customer did not start talking. Please try again.',
  /**
   * What the rep did with each buying signal, on the Playbook Live end screen.
   * Same words as the admin portal's Closing insights (owner, 2026-09-27: a
   * signal the call ended on read "pending").
   */
  closeSignalOutcome: {
    converted: 'converted',
    partial: 'partly used',
    missed: 'missed',
    pending: 'not answered',
  } as Record<string, string>,
  /** Playbook Live end screen when the voice connection dropped mid-call. */
  connectionDroppedTitle: 'Call dropped',
  connectionDropped:
    'The connection to the AI customer dropped, so this session ended early. It is saved as abandoned. Start a new session to try again.',
  /** Turn line while live (useTurnIndicator): the customer's audio is playing. */
  customerSpeaking: 'AI Speaking',
  repTurn: 'Your Turn to Speak',
  /** The rep spoke and went quiet; the customer has not answered yet. */
  processing: 'Processing...',
  processingSlow: 'Still working on it...',
  processingStalled: 'Taking longer than usual. Keep going or end the session.',
  ended: 'Session ended',
  end: 'End session',
  ending: 'Wrapping up...',
} as const;

/** Copy for the knowledge-base training session (TrainingSessionPage). */
export const KB_LIVE_COPY = {
  waitingForItem: 'The customer will raise their first question in a moment.',
  itemPosition: (index: number, total: number): string =>
    total > 0 ? `Item ${index} of ${total}` : `Item ${index}`,
  stillThereTitle: 'Still there?',
  stillThereBody: 'The customer is waiting for your answer. Just start talking when you are ready.',
  alreadyEnded:
    'This training session has already ended. Please start a new training session from the admin portal.',
  noTrack: 'This session has no training track attached. Go back and pick a track.',
  startFailed: 'We could not start the session. Check your microphone and try again.',
  completedTitle: 'Training session complete',
  completedAnswered: (answered: number, total: number): string =>
    `You answered ${answered} of ${total} items.`,
  completedScoring: 'Your session has been saved. Your answers are being scored, and the results will be ready shortly.',
  viewResults: TRAINING_CALL_COPY.viewResults,
} as const;

/** Display names for KB item types; an unknown type falls back to the raw value. */
export const KB_ITEM_TYPE_LABEL: Record<string, string> = {
  objection: 'Objection',
  faq: 'FAQ',
  landmine: 'Landmine',
  competitor: 'Competitor',
};
